import cn from 'classnames';
import React, {InputHTMLAttributes} from 'react';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
    className?: string;
    inputClassName?: string;
    label?: string;
    name: string;
    error?: string;
    type?: string;
    shadow?: boolean;
    variant?: 'normal' | 'solid' | 'outline' | 'line';
    dimension?: 'small' | 'medium' | 'big';
    required?: boolean;
}

const variantClasses = {
    normal: 'bg-KPZ_Gray-06 border border-border-base rounded focus:shadow focus:bg-primary-0 focus:border-accent',
    solid: 'bg-KPZ_Gray-06 border border-border-100 rounded focus:bg-primary-0 focus:border-accent',
    outline: 'border border-border-base rounded focus:border-accent',
    line: 'ltr:pl-0 rtl:pr-0 border-b border-border-base rounded-none focus:border-accent',
};

const sizeClasses = {
    small: 'text-sm h-10',
    medium: 'h-12',
    big: 'h-14',
};

const Input = React.forwardRef<HTMLInputElement, Props>(
    (
        {
            className,
            label,
            name,
            error,
            children,
            variant = 'normal',
            dimension = 'medium',
            shadow = false,
            disabled = false,
            type = 'text',
            inputClassName,
            required,
            ...rest
        },
        ref,
    ) => {
        return (
            <div className={className}>
                {label && (
                    <label htmlFor={name} className="mb-3 block text-sm font-semibold leading-none text-body-dark">
                        {label}
                        {required && <span className="text-red-500">*</span>}
                    </label>
                )}
                <input
                    id={name}
                    name={name}
                    type={type}
                    ref={ref}
                    className={cn(
                        'flex w-full appearance-none items-center px-4 text-sm text-heading transition duration-300 ease-in-out focus:outline-none focus:ring-0',
                        shadow && 'focus:shadow',
                        variantClasses[variant],
                        sizeClasses[dimension],
                        disabled && 'cursor-not-allowed bg-gray-100',
                        inputClassName,
                    )}
                    disabled={disabled}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    aria-invalid={error ? 'true' : 'false'}
                    {...rest}
                />
                {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
            </div>
        );
    },
);
Input.displayName = 'Input';
export default Input;
